// IND-009: tabla de cobertura del Panel (Materias primas / Embalajes), como
// la maqueta: cinco estados, compra pendiente con su nota, saldo final en rojo
// si es negativo y orden pulsando cualquier cabecera (IND-026).
import { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import type { MaterialStockProjectionRow } from '../types/industrialCost';
import {
  coverageState,
  formatQty,
  purchaseNote,
  sortCoverageRows,
} from '../utils/coverageState';
import type { CoverageSortKey, CoverageStateKey } from '../utils/coverageState';
import { realMaterialName, validName, validUnit } from '../utils/plantPanel';
import { sortRows, useTableSort } from '../utils/tableSort';
import SortableHeaderCell from '../components/SortableHeaderCell';

// IND-015: 10 filas visibles con scroll interno; las dos tablas, misma altura.
const HEADER_HEIGHT = 48;
const ROW_HEIGHT = 62;
const VISIBLE_ROWS = 10;

const STATE_STYLE: Record<CoverageStateKey, { bg: string; fg: string }> = {
  rotura: { bg: 'rgba(248,113,113,0.14)', fg: '#fca5a5' },
  confirmar: { bg: 'rgba(251,191,36,0.14)', fg: '#fcd34d' },
  tarde: { bg: 'rgba(251,191,36,0.14)', fg: '#fcd34d' },
  sin_stock: { bg: 'rgba(148,163,184,0.16)', fg: '#cbd5e1' },
  cubierto: { bg: 'rgba(74,222,128,0.12)', fg: '#86efac' },
};

// IND-026: «Estado» ascendente = lo más grave primero.
const STATE_RANK: Record<CoverageStateKey, number> = {
  rotura: 0,
  confirmar: 1,
  tarde: 2,
  sin_stock: 3,
  cubierto: 4,
};

type TableSortKey = CoverageSortKey | 'material' | 'state';

const SORT_COLUMNS: Array<{ key: CoverageSortKey; label: string }> = [
  { key: 'stock', label: 'Stock hoy' },
  { key: 'need', label: 'Necesidad' },
  { key: 'purchase', label: 'Compra pendiente' },
  { key: 'balance', label: 'Saldo final' },
];

type Props = {
  title: string;
  rows: MaterialStockProjectionRow[];
  calculationDate?: string | null;
  loading: boolean;
  onSelect: (row: MaterialStockProjectionRow) => void;
};

export default function IndustrialCostCoverageTable({ title, rows, calculationDate, loading, onSelect }: Props) {
  const [search, setSearch] = useState('');
  const sort = useTableSort<TableSortKey>('need', 'desc');

  const withState = useMemo(
    () => rows.map((row) => ({ row, state: coverageState(row, calculationDate) })),
    [rows, calculationDate],
  );
  const counts = {
    rotura: withState.filter((item) => item.state.key === 'rotura').length,
    revisar: withState.filter((item) => ['confirmar', 'tarde', 'sin_stock'].includes(item.state.key)).length,
    cubiertos: withState.filter((item) => item.state.key === 'cubierto').length,
  };

  const visible = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('es-ES');
    const filtered = term
      ? rows.filter((row) =>
          [row.material_name, row.material_code]
            .filter(Boolean)
            .some((value) => String(value).toLocaleLowerCase('es-ES').includes(term)),
        )
      : rows;
    const states = new Map(withState.map((item) => [item.row.material_id, item.state]));
    const stateOf = (row: MaterialStockProjectionRow) =>
      states.get(row.material_id) ?? coverageState(row, calculationDate);
    const sorted =
      sort.key === 'material'
        ? sortRows(filtered, (row) => validName(row.material_name) ?? row.material_code, sort.direction)
        : sort.key === 'state'
          ? sortRows(filtered, (row) => STATE_RANK[stateOf(row).key], sort.direction)
          : sortCoverageRows(filtered, sort.key, sort.direction);
    return sorted.map((row) => ({ row, state: stateOf(row) }));
  }, [rows, withState, search, sort.key, sort.direction, calculationDate]);

  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: 3,
        bgcolor: '#151f30',
        border: '1px solid rgba(148,163,184,0.16)',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <Typography sx={{ color: '#f8fafc', fontWeight: 800, fontSize: 16 }}>{title}</Typography>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={`${formatQty(counts.rotura)} con rotura`} sx={{ bgcolor: STATE_STYLE.rotura.bg, color: STATE_STYLE.rotura.fg, fontWeight: 700 }} />
          <Chip size="small" label={`${formatQty(counts.revisar)} a revisar`} sx={{ bgcolor: STATE_STYLE.confirmar.bg, color: STATE_STYLE.confirmar.fg, fontWeight: 700 }} />
          <Chip size="small" label={`${formatQty(counts.cubiertos)} cubiertos`} sx={{ bgcolor: STATE_STYLE.cubierto.bg, color: STATE_STYLE.cubierto.fg, fontWeight: 700 }} />
        </Stack>
      </Stack>

      <TextField
        size="small"
        label="Buscar"
        placeholder="Nombre o código"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        fullWidth
      />

      {/* IND-016: la tabla se ajusta al ancho de su caja (sin scroll lateral donde cabe; solo en pantallas muy estrechas, para no esconder Estado). */}
      <Box sx={{ overflowY: 'auto', overflowX: 'auto', height: HEADER_HEIGHT + ROW_HEIGHT * VISIBLE_ROWS }}>
        <Table
          size="small"
          stickyHeader
          sx={{
            width: '100%',
            '& td, & th': { borderColor: 'rgba(148,163,184,0.12)', px: 0.75 },
            '& thead th': { bgcolor: '#151f30', lineHeight: 1.2 },
            '& td': { height: ROW_HEIGHT, boxSizing: 'border-box' },
          }}
        >
          <TableHead>
            <TableRow>
              <SortableHeaderCell
                label="Material"
                columnKey="material"
                sort={sort}
                firstDirection="asc"
                sx={{ color: '#cbd5e1', fontSize: 12, width: '33%' }}
              />
              {SORT_COLUMNS.map((column) => (
                <SortableHeaderCell
                  key={column.key}
                  label={column.label}
                  columnKey={column.key}
                  sort={sort}
                  align="right"
                  sx={{ color: '#cbd5e1', fontSize: 12 }}
                />
              ))}
              <SortableHeaderCell
                label="Estado"
                columnKey="state"
                sort={sort}
                firstDirection="asc"
                sx={{ color: '#cbd5e1', fontSize: 12, width: '1%', whiteSpace: 'nowrap' }}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            ) : visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ color: '#94a3b8' }}>
                  {search.trim()
                    ? 'Ningún material coincide con la búsqueda.'
                    : 'Sin materiales con necesidad para este grupo.'}
                </TableCell>
              </TableRow>
            ) : (
              visible.map(({ row, state }) => {
                const note = purchaseNote(row);
                const style = STATE_STYLE[state.key];
                // IND-029: el nombre solo si es distinto del código.
                const name = realMaterialName(row.material_name, row.material_code);
                const unit = validUnit(row.unit_label);
                const balanceNegative = row.eventual_balance_qty != null && row.eventual_balance_qty < 0;
                return (
                  <TableRow
                    key={row.material_id}
                    hover
                    tabIndex={0}
                    aria-label={`Ver el detalle de ${name ?? row.material_code ?? 'este material'}`}
                    onClick={() => onSelect(row)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onSelect(row);
                      }
                    }}
                    sx={{ cursor: 'pointer', '&:focus-visible': { outline: '2px solid #38bdf8' } }}
                  >
                    <TableCell component="th" scope="row" sx={{ maxWidth: 0 }}>
                      {/* Nombre en dos líneas como máximo; completo al pasar el ratón */}
                      <Tooltip title={name ?? row.material_code ?? ''} placement="top-start" enterDelay={400}>
                        <Typography
                          sx={{
                            color: '#f8fafc',
                            fontWeight: 600,
                            fontSize: 13,
                            lineHeight: 1.25,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            wordBreak: 'break-word',
                          }}
                        >
                          {name ?? row.material_code ?? '—'}
                        </Typography>
                      </Tooltip>
                      <Typography noWrap sx={{ color: '#94a3b8', fontSize: 12 }}>
                        {/* El código no se repite si el material no tiene nombre */}
                        {[name ? row.material_code : null, unit].filter(Boolean).join(' · ')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#f8fafc', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {/* IND-020: marca en el stock; la frase, al pasar el ratón */}
                      {row.stock_explanation ? (
                        <Tooltip title={`${row.stock_explanation}.`} arrow>
                          <Box component="span" sx={{ color: '#fca5a5', cursor: 'help' }}>
                            {'⚠ '}
                            {formatQty(row.calculated_stock_qty)}
                          </Box>
                        </Tooltip>
                      ) : (
                        formatQty(row.calculated_stock_qty)
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#e2e8f0', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {formatQty(row.pending_requirement_qty)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#e2e8f0', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {formatQty(row.incoming_pending_qty)}
                      {note.text ? (
                        <Typography sx={{ fontSize: 11, color: note.warn ? '#fcd34d' : '#94a3b8' }}>{note.text}</Typography>
                      ) : null}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 700, color: balanceNegative ? '#fca5a5' : '#f8fafc', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                    >
                      {formatQty(row.eventual_balance_qty)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {/* IND-015: solo la etiqueta; el detalle, al pasar el ratón */}
                      <Tooltip title={state.detail || ''} disableHoverListener={!state.detail} arrow>
                        <Chip
                          size="small"
                          label={state.label}
                          sx={{ bgcolor: style.bg, color: style.fg, fontWeight: 700, fontSize: 11, '& .MuiChip-label': { px: 0.9 } }}
                        />
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Box>
      <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>
        Pulsa un material para ver sus OF, sus compras y el saldo día a día.
      </Typography>
    </Paper>
  );
}
